import { useEffect, useMemo, useState } from "react"
import { FieldType, FieldValue, FormField, FormSeparator, ModalId, O, option, useEffectAfterMount, useUIActions } from "../../../../../features/ui"
import { DeliveryData, DeliveryMethod } from "../../../state/types"
import { dateToString, Labels, useDescribeHooks } from "../../../../../features/data-types"
import { toDate } from "../../../../../utils/typeUtils"
import { TableName } from "../../../../../tables"
import { BranchBadge, useBranchLoaders } from "../../../../manager"
import { Customer } from "../../../../sales"
import { firstItem } from "../../../../../utils/listUtils"

type Props = {
    table: TableName
    defaultData?: Partial<DeliveryData>
    customer?: Customer
    addressIdValue?: string
    branchId: string
    isMethodOptional?: boolean
    isAddressOptional?: boolean
    isDeliveryDateOptional?: boolean
    showSeparator?: boolean
    onChange: (data: Partial<DeliveryData>) => void
}

export const DeliveryDataFields = (props: Props) => {
    const { table, defaultData, customer, addressIdValue, branchId, isMethodOptional = true, isAddressOptional = true, isDeliveryDateOptional = true, showSeparator = true, onChange } = props
    const isSale = useMemo(() => table === TableName.SALE_ORDERS, [table])
    const isPurchase = useMemo(() => table === TableName.PURCHASE_ORDERS, [table])

    const [method, setMethod] = useState<O<DeliveryMethod>>(defaultData?.method)
    const [addressId, setAddressId] = useState<O<string>>(addressIdValue || defaultData?.addressId)
    const [deliveryDate, setDeliveryDate] = useState<O<Date>>(toDate(defaultData?.deliveryDate))

    const { describe } = useDescribeHooks()

    const branchLoaders = useBranchLoaders()

    const { openModal } = useUIActions()

    const methodOptions = useMemo(() => [DeliveryMethod.PICKUP, DeliveryMethod.SHIPMENT].map(method => option(method, Labels.deliveryMethodLabel(method))), [])
    const addresses = useMemo(() => customer?.deliveryAddresses.filter(address => !address.archived || address.id === addressId) || [], [customer?.deliveryAddresses, addressId])
    const addressOptions = useMemo(() => addresses.map(address => option(address.id, describe(TableName.ADDRESSES, address))), [addresses])

    useEffect(() => {
        if (addressIdValue) {
            setAddressId(addressIdValue)
            onChangeField({ addressId: addressIdValue })
        }
    }, [addressIdValue])

    useEffectAfterMount(() => {
        if (method === DeliveryMethod.SHIPMENT && (!addressId ||!addresses.map(address => address.id).includes(addressId))) {
            const newAddressId = firstItem(addresses)?.id
            setAddressId(newAddressId)
            onChangeField({ addressId: newAddressId })
        }
    }, [addresses])

    const onChangeField = (newProps: Partial<DeliveryData>) => {
        const data: Partial<DeliveryData> = {
            method,
            addressId,
            deliveryDate: dateToString(deliveryDate)
        }
        onChange({
            ...data,
            ...newProps
        })
    }

    const onChangeMethod = (value?: FieldValue) => {
        const newMethod = value as O<DeliveryMethod>
        setMethod(newMethod)
        if (newMethod) {
            const branch = branchLoaders().findBranch(branchId)!
            const newAddressId = newMethod === DeliveryMethod.PICKUP ? branch.address.id : firstItem(addresses)?.id
            setAddressId(newAddressId)
            onChangeField({
                method: newMethod,
                addressId: newAddressId
            })
        }
    }

    const onChangeAddress = (value?: FieldValue) => {
        const newAddressId = value as O<string>
        setAddressId(newAddressId)
        if (newAddressId) {
            onChangeField({ addressId: newAddressId })
        }
    }

    const onChangeDeliveryDate = (value?: FieldValue) => {
        const newDeliveryDate = value as O<Date>
        setDeliveryDate(newDeliveryDate)
        if (newDeliveryDate) {
            onChangeField({ deliveryDate: dateToString(newDeliveryDate) })
        }
    }

    const onCreateCustomerAddress = () => openModal(ModalId.QUICK_FORM)

    const fields = useMemo(() => {
        const fieldsList: FormField[] = []
        if (!isPurchase) {
            const deliveryMethodField = {
                name: 'deliveryMethod',
                type: FieldType.SELECT,
                label: 'Método de Entrega',
                value: method,
                options: methodOptions,
                narrow: true,
                optional: isMethodOptional,
                onChange: onChangeMethod
            }
            fieldsList.push(deliveryMethodField)
        }
        if (isSale || isPurchase) {
            const deliveryDateField = {
                name: 'deliveryDate',
                type: FieldType.DATE,
                label: 'Fecha de Envío',
                value: deliveryDate,
                min: new Date(),
                optional: isDeliveryDateOptional,
                onChange: onChangeDeliveryDate
            }
            fieldsList.push(deliveryDateField)
        }

        if (showSeparator) {
            const separator = {
                name: 'delivery-separator',
                type: FieldType.TEXT,
                render: () => <FormSeparator title="Entrega" />
            }
            fieldsList.splice(0, 0, separator)
        }

        const branchField = {
            name: 'branchId',
            type: FieldType.TABLE,
            table: TableName.BRANCHES,
            label: `Sucursal${isSale ? ' de Retiro' : ''}`,
            hint: isSale || isPurchase ? 'Para modificar la sucursal debe crear la orden desde la sucursal deseada.' : undefined,
            render: () => <BranchBadge branchId={branchId} />
        }
        if (method === DeliveryMethod.PICKUP) {
            isSale && fieldsList.splice(2, 0, branchField)
        } else if (method === DeliveryMethod.SHIPMENT) {
            const addressField = {
                name: 'addressId',
                type: FieldType.SELECT,
                label: 'Dirección de Envío',
                value: addressId,
                options: addressOptions,
                narrow: true,
                optional: isAddressOptional,
                onChange: onChangeAddress,
                onCreateNew: onCreateCustomerAddress
            }
            fieldsList.splice(2, 0, addressField)
        }

        !isSale && fieldsList.splice(1, 0, branchField)

        return fieldsList
    }, [isSale, method, methodOptions, deliveryDate, branchId, addressId, addressOptions])

    return fields
}
